/** Package date-scope and capacity-range conflict rules. */

import { SectionMode } from '@domain/value-objects/PackageEnums';
import type {
  DayTypeValue,
  SeasonValue,
  SectionModeValue,
} from '@domain/value-objects/PackageEnums';

export interface PackageDateScope {
  readonly dayType: DayTypeValue;
  readonly season: SeasonValue;
}

export interface PackageVariantIdentity extends PackageDateScope {
  readonly sectionMode: SectionModeValue;
  readonly hasIncludedServices: boolean;
  readonly capacityMin: number;
  readonly capacityMax: number;
  readonly capacityMenMin?: number;
  readonly capacityMenMax?: number;
  readonly capacityWomenMin?: number;
  readonly capacityWomenMax?: number;
}

export function packageVariantsDuplicate(
  existing: PackageVariantIdentity,
  target: PackageVariantIdentity
): boolean {
  return (
    sameVariantIdentity(existing, target) &&
    existing.capacityMin === target.capacityMin &&
    existing.capacityMax === target.capacityMax &&
    existing.capacityMenMin === target.capacityMenMin &&
    existing.capacityMenMax === target.capacityMenMax &&
    existing.capacityWomenMin === target.capacityWomenMin &&
    existing.capacityWomenMax === target.capacityWomenMax
  );
}

/**
 * Same-identity ranges cannot overlap because one request would then match
 * multiple prices. Bounds are inclusive, so 70–100 overlaps 100–120.
 * A `both` package overlaps only when both section ranges overlap.
 */
export function packageVariantRangesConflict(
  existing: PackageVariantIdentity,
  target: PackageVariantIdentity
): boolean {
  if (!sameVariantIdentity(existing, target)) return false;

  if (existing.sectionMode !== SectionMode.BOTH) {
    return rangesOverlap(
      existing.capacityMin,
      existing.capacityMax,
      target.capacityMin,
      target.capacityMax
    );
  }

  return (
    rangesOverlap(
      existing.capacityMenMin!,
      existing.capacityMenMax!,
      target.capacityMenMin!,
      target.capacityMenMax!
    ) &&
    rangesOverlap(
      existing.capacityWomenMin!,
      existing.capacityWomenMax!,
      target.capacityWomenMin!,
      target.capacityWomenMax!
    )
  );
}

export function rangesOverlap(
  leftMin: number,
  leftMax: number,
  rightMin: number,
  rightMax: number
): boolean {
  return leftMin <= rightMax && rightMin <= leftMax;
}

function sameVariantIdentity(
  existing: PackageVariantIdentity,
  target: PackageVariantIdentity
): boolean {
  return (
    existing.sectionMode === target.sectionMode &&
    existing.dayType === target.dayType &&
    existing.season === target.season &&
    existing.hasIncludedServices === target.hasIncludedServices
  );
}

/** Different but intersecting day/season scopes remain forbidden. */
export function packageDateScopesConflict(
  existing: PackageDateScope,
  target: PackageDateScope
): boolean {
  if (
    existing.dayType === target.dayType &&
    existing.season === target.season
  ) {
    return false;
  }

  const dayTypeOverlaps =
    existing.dayType === target.dayType ||
    existing.dayType === 'all' ||
    target.dayType === 'all';
  const seasonOverlaps =
    existing.season === target.season ||
    existing.season === 'all' ||
    target.season === 'all';

  return dayTypeOverlaps && seasonOverlaps;
}
